import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Crown, Users, DollarSign, Shield, Check, X, AlertTriangle, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// Currency conversion utility - converts any currency to CAD for admin view
// Uses approximate exchange rates (update periodically or fetch from API)
const EXCHANGE_RATES_TO_CAD = {
  CAD: 1.00,
  USD: 1.40,   // 1 USD ≈ 1.40 CAD
  INR: 0.02,   // 1 INR ≈ 0.02 CAD (₹149 ≈ C$2.98)
  AED: 0.38,   // 1 AED ≈ 0.38 CAD
  AUD: 0.90,   // 1 AUD ≈ 0.90 CAD
  EUR: 1.50,   // 1 EUR ≈ 1.50 CAD
  GBP: 1.75,   // 1 GBP ≈ 1.75 CAD
};

const convertToCAD = (amount, currency) => {
  const rate = EXCHANGE_RATES_TO_CAD[currency?.toUpperCase()] || 1;
  return amount * rate;
};

const formatCurrency = (amount, currency) => {
  const symbols = {
    CAD: 'C$', USD: '$', INR: '₹', AED: 'AED ', 
    AUD: 'A$', EUR: '€', GBP: '£'
  };
  const symbol = symbols[currency?.toUpperCase()] || currency;
  return `${symbol}${amount.toFixed(2)}`;
};

export default function AdminSubscriptionsPage() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/api/admin/subscriptions/users'),
        api.get('/api/admin/subscriptions/stats'),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleWaive = async (userId, username) => {
    if (!confirm(`Grant free Pro access to ${username}?`)) return;

    setActionLoading(true);
    try {
      await api.post(`/api/admin/subscriptions/waive/${userId}`, {
        user_id: userId,
        reason: 'Admin granted free access',
      });
      toast.success(`Pro access granted to ${username}`);
      await load();
    } catch (error) {
      toast.error('Failed to grant access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (userId, username) => {
    if (!confirm(`Revoke free Pro access from ${username}?`)) return;

    setActionLoading(true);
    try {
      await api.delete(`/api/admin/subscriptions/waive/${userId}`);
      toast.success(`Pro access revoked from ${username}`);
      await load();
    } catch (error) {
      toast.error('Failed to revoke access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWaiveAll = async () => {
    if (!confirm('Grant FREE Pro access to ALL users? This will give everyone unlimited access.')) return;

    setActionLoading(true);
    try {
      const { data } = await api.post('/api/admin/subscriptions/waive-all', {
        reason: 'Admin granted free access to all users',
      });
      toast.success(`Pro access granted to ${data.users_updated} users!`);
      await load();
    } catch (error) {
      toast.error('Failed to grant access to all');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAll = async () => {
    if (!confirm('Revoke ALL free Pro access? Users will need to subscribe to continue using Pro features.')) return;

    setActionLoading(true);
    try {
      // IMPORTANT: use DELETE (not POST) — POST is the grant endpoint
      const { data } = await api.delete('/api/admin/subscriptions/waive-all');
      toast.success(`Waivers revoked from ${data.users_updated} users`);
      await load();
    } catch (error) {
      toast.error('Failed to revoke waivers');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-2">
          <Crown className="h-8 w-8 text-yellow-500" />
          Subscription Management
        </h1>
        <p className="text-muted-foreground mt-1">Manage user subscriptions and grant free access</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Crown className="h-4 w-4 text-yellow-500" />
              Pro Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.active_subscribers || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.paying_subscribers || 0} paying • {stats?.waived_users || 0} free
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Revenue (in CAD)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">C${stats?.total_revenue?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-muted-foreground">Converted from all currencies</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              MRR (in CAD)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">C${stats?.mrr?.toFixed(2) || '0.00'}</div>
            <p className="text-xs text-muted-foreground">Monthly Recurring Revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      <Card className="border-2 border-primary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Bulk Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button
              onClick={handleWaiveAll}
              disabled={actionLoading}
              variant="default"
              className="flex-1"
            >
              <Check className="h-4 w-4 mr-2" />
              Grant Free Pro to ALL Users
            </Button>
            <Button
              onClick={handleRevokeAll}
              disabled={actionLoading}
              variant="destructive"
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Revoke ALL Free Access
            </Button>
          </div>
          <p className="text-xs text-muted-foreground flex items-start gap-2">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              <strong>Grant Free Pro to ALL:</strong> Gives every user unlimited Pro access without payment. 
              <strong className="ml-2">Revoke ALL:</strong> Removes free access; users will need to subscribe.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">User</th>
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-left py-2 px-3">Effective Access</th>
                  <th className="text-left py-2 px-3">Trial</th>
                  <th className="text-left py-2 px-3">DB Status</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.name || user.username}</span>
                        {user.supporter_badge && (
                          <Badge variant="secondary" className="text-xs">
                            <Crown className="h-3 w-3 mr-1" />
                            Supporter
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-muted-foreground">{user.email || '-'}</td>
                    <td className="py-3 px-3">
                      <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-3">
                      {user.role === 'admin' ? (
                        <Badge className="bg-purple-600">Full Access</Badge>
                      ) : user.subscription_waived ? (
                        <Badge className="bg-green-600">FREE (Admin)</Badge>
                      ) : user.has_pro_access ? (
                        user.is_trial_active ? (
                          <Badge className="bg-blue-600">Trial Active</Badge>
                        ) : (
                          <Badge className="bg-green-600">Pro (Paid)</Badge>
                        )
                      ) : (
                        <Badge variant="outline">No Access</Badge>
                      )}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground text-xs">
                      {user.role === 'admin'
                        ? 'Superuser'
                        : user.is_trial_active
                        ? `${user.trial_days_left}d left`
                        : user.subscription_waived
                        ? 'N/A (waived)'
                        : user.has_pro_access
                        ? 'Expired (paid active)'
                        : 'Expired'}
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">
                      <span title={`access_reason: ${user.access_reason || 'unknown'}`}>
                        {user.subscription_status || 'none'}
                        {user.access_reason && user.access_reason !== 'none' && (
                          <span className="ml-1 text-[10px] text-primary opacity-70">({user.access_reason})</span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {user.role !== 'admin' && (
                        user.subscription_waived ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRevoke(user.id, user.username)}
                            disabled={actionLoading}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleWaive(user.id, user.username)}
                            disabled={actionLoading}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Grant Free
                          </Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
